'use client';

import {
  Asterisk,
  BracketsCurly,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  Check,
  CircleHalfTilt,
  Hash,
  type IconProps,
  Key,
  ShoppingCart,
  TextAa,
  Truck,
  TShirt,
  X,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';

import { cn } from '@skrym/shared/utils/cn';

import {
  getEndpointData,
  type OpenAPIPathItem,
  type OpenAPIResponses,
  type OpenAPISchema,
} from '@/client/openAPI';

import { tailwindColorClasses, tailwindColorHoverClasses } from './chip';

/* #### Response

| Code                              | Description                                                     |
| --------------------------------- | --------------------------------------------------------------- |
| ✅ - [200](#200-response)         | Successful Advanced                                             |
| ✅ - [200](#200-response-partial) | Successful Partial or Basic                                     |
| 🚨 - [400](#400-response)         | Invalid argument provided, could be due to missing product info |
| 🚨 - [401](#401-response)         | Unauthenticated user, token may not be valid or has expired     |
| 🚨 - [500](#500-response)         | Internal error, contact Skrym staff if persistent               |

*/

type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

export interface JsonParameter {
  name: string;
  type: 'string' | 'int' | 'float' | string;
  description: string;
  required?: boolean;
  array?: boolean;
  children?: JsonParameter[];
}

interface HttpResponse {
  code: number;
  label: string;
  description: string;
  responseBody: JsonParameter[];
}

interface EndpointProps {
  path?: string;
  method?: HttpMethod;
  overrideDescription?: string;
  baseUrl?: string;
  headers?: JsonParameter[];
  noAuth?: boolean;
}

interface HttpMethodConfig {
  class: string;
}

const httpMethodConfig: Record<HttpMethod, HttpMethodConfig> = {
  get: {
    class: tailwindColorClasses.blue,
  },
  post: {
    class: tailwindColorClasses.green,
  },
  put: {
    class: tailwindColorClasses.yellow,
  },
  delete: {
    class: tailwindColorClasses.red,
  },
  patch: {
    class: tailwindColorClasses.purple,
  },
};

function sortParameters(params: JsonParameter[]): JsonParameter[] {
  return params.sort((a, b) => {
    // Priority for 'id'
    if (a.name.toLowerCase() === 'id') return -1;
    if (b.name.toLowerCase() === 'id') return 1;

    // Priority for names containing 'id'
    const idRegex = /id/i;
    if (idRegex.test(a.name)) return -1;
    if (idRegex.test(b.name)) return 1;

    // Priority for names containing 'name'
    if (a.name.includes('name')) return -1;
    if (b.name.includes('name')) return 1;

    // Lower priority for 'created' or 'updated' with 'date-time' type
    if ((a.name.includes('created') || a.name.includes('updated')) && a.type === 'date-time')
      return 1;
    if ((b.name.includes('created') || b.name.includes('updated')) && b.type === 'date-time')
      return -1;

    // Default to alphabetical sorting for other parameters
    return a.name.localeCompare(b.name);
  });
}

function convertToResponses(openApiResps: OpenAPIResponses): HttpResponse[] {
  const parsedResponses: HttpResponse[] = [];

  Object.entries(openApiResps).forEach(([key, prop]) => {
    const parsedRes: HttpResponse = {
      code: key === 'default' ? 500 : Number.parseInt(key),
      label: '',
      description: prop?.description,
      responseBody: prop.content
        ? convertToJSONParameter(prop.content['application/json'].schema)
        : [],
    };

    parsedResponses.push(parsedRes);
  });

  return parsedResponses;
}

export function convertToJSONParameter(schema: OpenAPISchema, level = 0): JsonParameter[] {
  const jsonParameters: JsonParameter[] = [];

  if (level >= 10) {
    return jsonParameters;
  }

  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, prop]) => {
      const isRequired = schema.required?.includes(key) ?? false;

      const jsonParameter: JsonParameter = {
        name: key,
        type: prop.type,
        description: prop.title || '',
        children: [],
        required: isRequired,
      };

      if (jsonParameter.type === 'string' && prop.format === 'uuid') {
        jsonParameter.type = 'uuid';
      } else if (jsonParameter.type === 'string' && prop.format === 'date-time') {
        jsonParameter.type = 'date-time';
      }

      if (jsonParameter.type === 'array') {
        jsonParameter.array = true;
        // Retaining the 'array' type
        jsonParameter.type = 'array';
        // Recursively calling conversion for children
        if (prop.items && ['array', 'object'].includes(prop.items.type)) {
          jsonParameter.children = convertToJSONParameter(prop.items, level + 1);
        } else if (
          prop.items &&
          ['number', 'integer', 'string', 'boolean', 'float'].includes(prop.items.type)
        ) {
          jsonParameter.type = prop.items.type;
        }
      }

      if (jsonParameter.type === 'object') {
        // Retaining the 'object' type
        jsonParameter.type = 'object';
        // Recursively calling conversion for children
        if (prop.properties) {
          Object.entries(prop.properties).forEach(([childKey, childProp]) => {
            const childIsOptional = !prop.required?.includes(childKey);

            const outcome: JsonParameter[] = convertToJSONParameter(
              {
                type: 'object',
                properties: { [childKey]: childProp },
              },
              level + 1,
            );

            // if we only get one parameter, we can safely add the required flag
            if (outcome.length === 1) {
              outcome[0].required = !childIsOptional;
            }

            if (jsonParameter.children) {
              jsonParameter.children.push(...outcome);
            }
          });
        }
      }

      jsonParameters.push(jsonParameter);
    });
  }

  return sortParameters(jsonParameters);
}

function Endpoint(props: EndpointProps) {
  const { path, overrideDescription, baseUrl, headers, method, noAuth } = props;

  const defaultHeaders: JsonParameter[] = [
    {
      name: 'Authorization',
      type: 'string',
      description:
        'Bearer token used for authenticating the request.\n\n Example of a token to send: Bearer 55CC7A69B46190A6F094...',
      required: true,
    },
  ];

  const [adjustedHeaders] = useState(headers ? headers : defaultHeaders);
  const [chosenMethod, _setChosenMethod] = useState<HttpMethod>(method ? method : 'post');

  const [isOpen, setIsOpen] = useState(false);
  const [isBodyVisible, setIsBodyVisible] = useState(true);
  const [isHeadersVisible, setIsHeadersVisible] = useState(false);
  const [isResponseVisible, setIsResponseVisible] = useState(true);

  const [activeParams, setActiveParams] = useState<Set<string>>(new Set());
  const [activeResponse, setActiveResponse] = useState<number | undefined>(200);
  const [hoveredResponse, setHoveredResponse] = useState<number>();

  const [endpointData, setEndpointData] = useState<OpenAPIPathItem | null>(null);

  useEffect(() => {
    // biome-ignore lint/style/noNonNullAssertion: FIXME: When touching this code, please remove this non-null assertion
    getEndpointData(path!).then(setEndpointData);
  }, [path]);

  // Assume empty object when endpointData is null
  const availableMethods = useMemo(() => Object.keys(endpointData || {}), [endpointData]);

  // Moved to the top level
  const reqBodySchema: OpenAPISchema | undefined = useMemo(() => {
    if (availableMethods.includes(chosenMethod)) {
      return endpointData?.[chosenMethod]?.requestBody?.content['application/json'].schema;
    }
    return undefined;
  }, [availableMethods, chosenMethod, endpointData]);

  const mappedResponses: HttpResponse[] = useMemo(() => {
    if (availableMethods.includes(chosenMethod) && endpointData?.[chosenMethod]?.responses) {
      // biome-ignore lint/style/noNonNullAssertion: FIXME: When touching this code, please remove this non-null assertion
      return convertToResponses(endpointData?.[chosenMethod]?.responses!);
    }
    return [];
  }, [availableMethods, chosenMethod, endpointData]);

  const mappedReqBodyParams: JsonParameter[] = useMemo(() => {
    if (reqBodySchema) {
      return convertToJSONParameter(reqBodySchema);
    }
    return [];
  }, [reqBodySchema]);

  const endpointDescription: string | undefined = useMemo(() => {
    if (availableMethods.includes(chosenMethod)) {
      return endpointData?.[chosenMethod]?.description;
    }
    return undefined;
  }, [availableMethods, chosenMethod, endpointData]);

  const activeResponseObject = useMemo(
    () => mappedResponses?.find((response) => response.code === activeResponse),
    [activeResponse, mappedResponses],
  );

  const toggleActiveParam = (param: string) => {
    const newActiveParams = new Set(activeParams);
    if (newActiveParams.has(param)) {
      newActiveParams.delete(param);
    } else {
      newActiveParams.add(param);
    }
    setActiveParams(newActiveParams);
  };

  const renderTypeChip = (
    type: 'string' | 'uuid' | 'int' | 'float' | 'number' | 'boolean' | 'date-time' | string,
    array: boolean,
  ) => {
    let Icon: React.ReactNode;
    const Props: IconProps = { weight: 'bold', size: 12, opacity: 0.7 };
    switch (type) {
      case 'string':
        Icon = <TextAa {...Props} />;
        break;
      case 'uuid':
        Icon = <Key {...Props} />;
        break;
      case 'int':
      case 'integer':
      case 'float':
      case 'number':
        Icon = <Hash {...Props} />;
        break;
      case 'boolean':
        Icon = <CircleHalfTilt {...Props} />;
        break;
      case 'object':
        Icon = <BracketsCurly {...Props} />;
        break;
      case 'transportMethod':
        Icon = <Truck {...Props} />;
        break;
      case 'order':
        Icon = <ShoppingCart {...Props} />;
        break;
      case 'date-time':
        Icon = <CalendarBlank {...Props} />;
        break;
      default:
        Icon = <TShirt {...Props} />;
    }
    return (
      <div className="flex items-center bg-neutral-100 text-neutral-500 dark:bg-neutral-800/50 text-xs  font-mono px-2 py-.9 rounded-md">
        {Icon}
        <span className="ml-1">
          {type}
          {array && '[]'}
        </span>
      </div>
    );
  };

  const renderParams = (
    parameters: JsonParameter[] | undefined,
    indentLevel = 0,
    isResponseType = 'false',
  ): React.ReactNode => {
    // return null if parameters is undefined
    if (!parameters) {
      return null;
    }

    return (
      <div
        className={cn(
          indentLevel > 0 &&
            `ml-${2 * indentLevel} my-3 py-3 border-l border-t border-b rounded-lg`,
          isResponseType === 'false' && 'dark:border-neutral-800 border-neutral-200',
          isResponseType === 'success' &&
            `dark:border-green-900/40 border-green-600/30  ml-${2 * (indentLevel + 1)}`,
        )}
      >
        {parameters.map((param) => {
          const isOptional: boolean = !param.required || isResponseType !== 'false';
          return (
            <div key={`${param.name}-${indentLevel}-${isResponseType}`}>
              {/** biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept */}
              <div
                className={`grid grid-cols-4 gap-x-4 items-start ${
                  indentLevel === 0 ? 'mt-3' : 'mt-2'
                }`}
                onClick={() => toggleActiveParam(`${param.name}-${indentLevel}-${isResponseType}`)}
                onKeyDown={() =>
                  toggleActiveParam(`${param.name}-${indentLevel}-${isResponseType}`)
                }
                style={{ marginLeft: `${indentLevel * 10}px` }}
              >
                <div className="col-span-2">
                  <div />
                  <div className="flex items-center">
                    {activeParams.has(`${param.name}-${indentLevel}-${isResponseType}`) ? (
                      <CaretDown
                        size={16}
                        color={param.children && param.children.length > 0 ? 'gray' : 'transparent'}
                      />
                    ) : (
                      <CaretRight
                        size={16}
                        color={param.children && param.children.length > 0 ? 'gray' : 'transparent'}
                      />
                    )}
                    <span
                      key={`${param.name}-${indentLevel}-${isResponseType}`}
                      className="font-mono mr-1 ml-2"
                    >
                      {param.name}
                    </span>{' '}
                    {!isOptional ? <Asterisk size={10} color="#ef4444" weight="bold" /> : null}
                  </div>
                  <div className={'flex flex-start ml-6'}>
                    {renderTypeChip(param.type, param.array ? param.array : false)}
                  </div>
                </div>
                <p className="col-span-2 text-sm mr-3 text-neutral-500">
                  {param.description ? param.description : '-'}
                </p>
              </div>
              {activeParams.has(`${param.name}-${indentLevel}-${isResponseType}`) &&
                param.children &&
                param.children.length > 0 &&
                renderParams(param.children, indentLevel + 1, isResponseType)}
            </div>
          );
        })}
      </div>
    );
  };

  const getSelectedResponseColor = (responseCode: number) =>
    responseCode < 300 ? tailwindColorClasses.green : tailwindColorClasses.red;

  const getSelectedHoverResponseColor = (responseCode: number) =>
    responseCode < 300 ? tailwindColorHoverClasses.green : tailwindColorHoverClasses.red;

  const renderResponses = (resps: HttpResponse[]) => {
    if (!resps) return null;

    // Sort responses by 'code' in ascending order
    const sortedResponses = [...resps].sort((a, b) => a.code - b.code);

    return (
      <div className="flex items-center flex-start gap-1 ml-.4 mt-2">
        {sortedResponses.map((response) => {
          const isErrResponse: boolean = response.code >= 300;

          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept
            <div
              onClick={() =>
                setActiveResponse(activeResponse === response.code ? undefined : response.code)
              }
              onKeyDown={() =>
                setActiveResponse(activeResponse === response.code ? undefined : response.code)
              }
              key={response.code}
              className={cn(
                'border rounded-lg border-neutral-200 dark:border-neutral-800 px-3 py-1',
                activeResponse === response.code && getSelectedResponseColor(response.code),
                hoveredResponse === response.code && getSelectedHoverResponseColor(response.code),
              )}
              onMouseEnter={() => setHoveredResponse(response.code)}
              onMouseLeave={() => setHoveredResponse(undefined)}
            >
              <div className="flex items-center flex-start">
                {isErrResponse ? (
                  <X weight="bold" size={16} className="text-red-500" />
                ) : (
                  <Check weight="bold" size={16} className="text-green-500" />
                )}
                <span className="ml-2 font-bold">{response.code}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={
        'mt-2 mb-2 [&:not(:first-child)]:mt-6 border rounded-lg border-neutral-200 bg-card dark:border-neutral-800 p-4 dark:hover:border-neutral-700 hover:bg-card-hover transition-all ease-in duration-150 hover:shadow-md cursor-pointer'
      }
    >
      {/** biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept */}
      <div
        className="mb-2 flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <span
            className={cn(
              'font-bold uppercase px-2 py-1 rounded-xl text-sm mr-2',
              httpMethodConfig[chosenMethod].class,
            )}
          >
            {chosenMethod}
          </span>
          <span className="break-all">
            <span className="text-neutral-400 dark:text-neutral-700">
              {baseUrl ? baseUrl : 'api.skrym.com/'}
            </span>
            <span className="font-semibold">{path}</span>
          </span>
        </div>
        <div className="text-neutral-500">
          {isOpen ? <CaretDown weight="bold" size={20} /> : <CaretLeft weight="bold" size={20} />}
        </div>
      </div>
      {isOpen && (
        <div>
          <p className="whitespace-pre-line">
            {overrideDescription
              ? overrideDescription
              : // Break out this into its own default text
                endpointDescription?.replace(/\\/g, '')}
          </p>
          <div className="font-medium text-sm text-neutral-400 dark:text-neutral-600 uppercase tracking-widest">
            Request
          </div>
          {!noAuth && adjustedHeaders && (
            <div className="cursor-pointer">
              <h4
                className="font-medium gap-1 mt-1 rounded-xs p-1 cursor-pointer flex items-center dark:hover:bg-neutral-800 hover:bg-neutral-100"
                onClick={() => setIsHeadersVisible(!isHeadersVisible)}
                onKeyDown={() => setIsHeadersVisible(!isHeadersVisible)}
              >
                {isHeadersVisible ? (
                  <CaretDown weight="bold" size={14} />
                ) : (
                  <CaretRight weight="bold" size={14} />
                )}
                Headers
              </h4>
              {isHeadersVisible && renderParams(adjustedHeaders)}
            </div>
          )}
          {mappedReqBodyParams && mappedReqBodyParams.length > 0 ? (
            <div>
              <h4
                className="font-medium gap-1 mt-1 rounded-xs p-1 cursor-pointer flex items-center dark:hover:bg-neutral-800 hover:bg-neutral-100"
                onClick={() => setIsBodyVisible(!isBodyVisible)}
                onKeyDown={() => setIsBodyVisible(!isBodyVisible)}
              >
                {isBodyVisible ? (
                  <CaretDown weight="bold" size={14} />
                ) : (
                  <CaretRight weight="bold" size={14} />
                )}
                Request body
              </h4>
              {isBodyVisible && renderParams(mappedReqBodyParams)}
            </div>
          ) : (
            <div>
              <p className="text-neutral-500 mt-0 italic">No request body is required</p>
            </div>
          )}
          <div className="mt-4">
            <div className="border-t border-neutral-300 dark:border-neutral-700 w-full my-4" />
            <div className="font-medium text-sm text-neutral-400 dark:text-neutral-600 uppercase tracking-widest">
              Responses
            </div>
            {mappedResponses && renderResponses(mappedResponses)}
            {activeResponse !== undefined && (
              <div>
                {activeResponseObject && activeResponseObject?.responseBody.length > 0 ? (
                  <div>
                    <h4
                      className="font-medium gap-1 mt-2 cursor-pointer inline-flex p-1 rounded-md items-center dark:hover:bg-neutral-800 hover:bg-neutral-100"
                      onClick={() => setIsResponseVisible(!isResponseVisible)}
                      onKeyDown={() => setIsResponseVisible(!isResponseVisible)}
                    >
                      {isResponseVisible ? (
                        <CaretDown weight="bold" size={14} />
                      ) : (
                        <CaretRight weight="bold" size={14} />
                      )}
                      Response body
                    </h4>
                    {isResponseVisible && (
                      <div
                        className={cn(
                          'px-1 pt-1 pb-3 rounded-md',
                          activeResponseObject?.code < 300
                            ? 'bg-green-500/5 border border-green-700/10 dark:border-green-200/10'
                            : 'bg-red-500/5 border border-red-700/10 dark:border-red-200/10',
                        )}
                      >
                        {renderParams(
                          activeResponseObject?.responseBody,
                          0,
                          activeResponseObject?.code < 300 ? 'success' : 'error',
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-2 text-neutral-500">No response body</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Endpoint;
